/**
 * Actions taken in Your turn wait here before anything is sent (spec 154
 * US2). Pure, so the rules are tested without timers or a page.
 *
 * - Every action is held for `HOLD_MS`. Undo before then removes it, and
 *   nothing was sent: no move, no notification, no trigger, no finished batch.
 * - When the time is up it is `sending` and can no longer be undone.
 * - Once sent it stays `sent` until its tasks leave the queue, so a row does
 *   not flash its buttons back between the answer and the next read.
 * - A refusal's words stay on the task's row until the row is acted on again.
 * - A task is in at most one live action, so it cannot be decided twice.
 */

/** How long an action waits, decided 2026-10-10. */
export const HOLD_MS = 6_000;

export type HeldKind = 'decide' | 'batch' | 'run-again' | 'start-subtasks';

export type HeldState = 'held' | 'sending' | 'sent';

export interface HeldAction {
  id: string;
  kind: HeldKind;
  /** One task, or a batch's siblings. */
  taskIds: readonly string[];
  /** The words on the row: "Done", "Done all 3", "Run again". */
  label: string;
  /** When it is sent: taken + `HOLD_MS`. */
  sendAt: number;
  state: HeldState;
}

export interface HeldActions {
  actions: readonly HeldAction[];
  /** A refusal's words, by task. */
  refusals: Readonly<Record<string, string>>;
}

export const NO_HELD_ACTIONS: HeldActions = { actions: [], refusals: {} };

/** The live action a task is in, if any. */
export function actionFor(
  state: HeldActions,
  taskId: string,
): HeldAction | undefined {
  return state.actions.find((action) => action.taskIds.includes(taskId));
}

/**
 * Hold a new action. Refused (state unchanged) when any of its tasks is
 * already in one. Taking it clears those tasks' earlier refusals: the person
 * has acted again.
 */
export function take(
  state: HeldActions,
  action: Omit<HeldAction, 'state' | 'sendAt'>,
  now: number,
): HeldActions {
  if (action.taskIds.some((taskId) => actionFor(state, taskId))) return state;
  const refusals = { ...state.refusals };
  for (const taskId of action.taskIds) delete refusals[taskId];
  return {
    actions: [
      ...state.actions,
      { ...action, state: 'held', sendAt: now + HOLD_MS },
    ],
    refusals,
  };
}

/** Undo: only while held. Once sending, nothing can call it back. */
export function undo(state: HeldActions, id: string): HeldActions {
  const action = state.actions.find((each) => each.id === id);
  if (action?.state !== 'held') return state;
  return { ...state, actions: state.actions.filter((each) => each.id !== id) };
}

/** The time is up: it is being sent. */
export function expire(state: HeldActions, id: string): HeldActions {
  return {
    ...state,
    actions: state.actions.map((each) =>
      each.id === id && each.state === 'held'
        ? { ...each, state: 'sending' }
        : each,
    ),
  };
}

/** Every held action, at once: the page is going away. Returns their ids too. */
export function flush(state: HeldActions): {
  state: HeldActions;
  ids: string[];
} {
  const ids = state.actions
    .filter((each) => each.state === 'held')
    .map((each) => each.id);
  let next = state;
  for (const id of ids) next = expire(next, id);
  return { state: next, ids };
}

/**
 * The answer arrived. Accepted tasks stay `sent` until they leave the queue;
 * refused tasks leave the action, keeping their words. An action left with
 * no accepted task is gone.
 */
export function settle(
  state: HeldActions,
  id: string,
  outcomes: Readonly<
    Record<string, { ok: true } | { ok: false; message: string }>
  >,
): HeldActions {
  const refusals = { ...state.refusals };
  const actions: HeldAction[] = [];
  for (const each of state.actions) {
    if (each.id !== id) {
      actions.push(each);
      continue;
    }
    const accepted = each.taskIds.filter((taskId) => {
      const outcome = outcomes[taskId];
      if (outcome && !outcome.ok) refusals[taskId] = outcome.message;
      return outcome?.ok === true;
    });
    if (accepted.length > 0) {
      actions.push({ ...each, taskIds: accepted, state: 'sent' });
    }
  }
  return { actions, refusals };
}

/** Forget sent actions whose tasks have all left the queue. */
export function prune(
  state: HeldActions,
  present: ReadonlySet<string>,
): HeldActions {
  const actions = state.actions.filter(
    (each) =>
      each.state !== 'sent' ||
      each.taskIds.some((taskId) => present.has(taskId)),
  );
  return actions.length === state.actions.length
    ? state
    : { ...state, actions };
}
